<template>
  <div>
    <div
      class="bg-background-neutral-secondary w-full space-y-3 rounded-2xl p-4"
    >
      <slot></slot>
      <div class="text-text-neutral-primary space-y-2 text-lg">
        <h2 class="font-bold">
          {{ seatMapLib.getDirectionText(flight.segments) }}
        </h2>
        <p>{{ getFormattedDate(flight.segments) }}</p>
      </div>
      <p
        v-if="selectedSeatsList"
        class="text-text-positive-primary flex w-full justify-between text-base"
      >
        <span>
          {{
            t('ancillaries.seatMap.seatsList', { seatsList: selectedSeatsList })
          }}
        </span>
        <span class="whitespace-nowrap font-semibold">{{
          flightSeatsPrice
        }}</span>
      </p>
      <FreeButton
        type="button"
        class="w-full"
        :class="
          hasFlightSelectedSeats || !hasSeatMaps
            ? 'gray-button'
            : 'green-main-button'
        "
        size="sm"
        :disabled="!hasSeatMaps"
        @click="bottomSheet.show(seatMapBottomSheetName)"
      >
        <template v-if="hasSeatMaps">
          {{
            hasFlightSelectedSeats
              ? t('ancillaries.seatMap.changeSeats')
              : t('ancillaries.seatMap.selectSeats')
          }}
        </template>
        <template v-else>
          {{ t('ancillaries.seatMap.noSeatMapForFlight') }}
        </template>
      </FreeButton>
    </div>
    <SeatMapBottomSheetContainer
      :name="seatMapBottomSheetName"
      :flight="flight"
      :seat-map-queries="seatMapQueries"
      :passengers-data="passengersData"
      :seat-map-state-by-flight="seatMapStateByFlight"
      :passengers-keys="passengersKeys"
      @onCommitState="emit('onCommitState', $event)"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { v4 as uuid } from 'uuid';
  import { FreeButton, type flightEntity } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import { prettyPrice } from '@aviata/text-fns';
  import { useI18n } from 'vue-i18n';
  import type { QueryObserverResult } from '@tanstack/vue-query';
  import type { PassengerKey, PassengersData } from '@aviata/booking-data';
  import type { Airflow } from '@aviata/platform-types';
  import type { AxiosResponse } from 'axios';

  import { seatMapLib } from './lib';
  import SeatMapBottomSheetContainer from './SeatMapBottomSheetContainer.vue';
  import type { SelectedSeatsState } from './types';

  const props = defineProps<{
    isMultiTripSearch?: boolean;
    flightIdx: number;
    flight: flightEntity.types.MappedFlight;
    seatMapQueries: QueryObserverResult<
      AxiosResponse<Airflow.SeatMap.Entity>
    >[];
    passengersData: PassengersData;
    seatMapStateByFlight: SelectedSeatsState;
    passengersKeys: PassengerKey[];
  }>();

  const emit = defineEmits<{
    onCommitState: [SelectedSeatsState];
  }>();

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();
  const seatMapBottomSheetName = `seat-map-bottom-sheet-${uuid()}`;
  const selectedSeatsList = computed(() => {
    return props.seatMapStateByFlight
      .filter(segmentState =>
        seatMapLib.hasSegmentSeats({
          passengersKeys: props.passengersKeys,
          seatMapState: segmentState,
        }),
      )
      .map(segmentState => {
        return props.passengersKeys
          .filter(key => seatMapLib.hasPassengerSeat(segmentState, key))
          .map(key => segmentState[key].seatKey);
      })
      .join(', ');
  });

  const flightSeatsPrice = computed(() => {
    const seatsPrice = seatMapLib.getSeatMapPrice(
      props.seatMapStateByFlight,
      props.passengersKeys,
    );
    return seatsPrice > 0
      ? `${prettyPrice(seatsPrice.toString())} ₸`
      : t('general.price.free');
  });

  const hasFlightSelectedSeats = computed(() => {
    return props.flight.segments.some((_, idx) =>
      seatMapLib.hasSegmentSeats({
        seatMapState: props.seatMapStateByFlight[idx],
        passengersKeys: props.passengersKeys,
      }),
    );
  });

  const hasSeatMaps = computed(() => {
    return props.seatMapQueries.every(
      query => Boolean(query.data?.data) || query.isLoading,
    );
  });

  function getFormattedDate(segments: flightEntity.types.MappedSegment[]) {
    const departure = segments[0].dep;
    return `${departure.dayOfMonth} • ${departure.time}`;
  }
</script>
